/**
 * Shared helpers for opening asset documents from IPFS (legal review, My assets, etc.).
 */

export function assetDocumentRowKey(d) {
  const cid = String(d?.ipfsCid ?? d?.IpfsCid ?? '').trim();
  return String(d?.id ?? d?.Id ?? d?.fileName ?? d?.FileName ?? cid ?? 'row');
}

function headerContentMime(headers) {
  const raw = headers?.['content-type'] || headers?.['Content-Type'] || '';
  const base = raw.split(';')[0].trim().toLowerCase();
  return base || '';
}

function sniffMimeFromU8(u8) {
  if (u8.length >= 4 && u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) {
    return 'image/png';
  }
  if (u8.length >= 3 && u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff) {
    return 'image/jpeg';
  }
  if (u8.length >= 4 && u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46) {
    return 'application/pdf';
  }
  if (u8.length >= 4 && u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x38) {
    return 'image/gif';
  }
  if (u8.length >= 12 && u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46) {
    return 'image/webp';
  }
  return '';
}

/** Build a Blob with a concrete MIME from axios IPFS download response + document row. */
export async function buildAssetDocumentPreviewBlob(response, doc) {
  const data = response.data;
  let ab;
  if (data instanceof Blob) {
    ab = await data.arrayBuffer();
  } else if (data instanceof ArrayBuffer) {
    ab = data;
  } else if (ArrayBuffer.isView(data)) {
    const v = data;
    ab = v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength);
  } else {
    throw new Error('Unexpected IPFS response body');
  }

  if (!ab || ab.byteLength === 0) {
    throw new Error('Downloaded file is empty');
  }

  let mime = headerContentMime(response.headers);
  if (!mime || mime === 'application/octet-stream') {
    const hint = String(doc?.documentType ?? doc?.DocumentType ?? '')
      .trim()
      .toLowerCase();
    if (hint.includes('/')) {
      mime = hint;
    }
  }
  const u8 = new Uint8Array(ab);
  if (!mime || mime === 'application/octet-stream') {
    mime = sniffMimeFromU8(u8.subarray(0, 16)) || 'application/octet-stream';
  }

  return new Blob([ab], { type: mime });
}

/**
 * Open blob URL: new tab for images/PDF, download for other types.
 * Avoid about:blank + img src=blob (often blank in modern browsers).
 */
export function openAssetDocumentObjectUrl(objectUrl, mime, doc) {
  const fileName = String(doc?.fileName ?? doc?.FileName ?? 'document').trim() || 'document';
  const type = String(mime || '').toLowerCase();
  const isImage = type.startsWith('image/');
  const isPdf = type === 'application/pdf';

  if (!isImage && !isPdf) {
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch {
      /* fall through */
    }
  }

  const handle = window.open(objectUrl, '_blank', 'noopener,noreferrer');
  return Boolean(handle);
}
